import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  createTeamInvite,
  getAdminTeamMemberships,
  getAdminTeams,
  getTeamInvites,
  getTeamMemberships,
  grantAdminTeamMembership,
  grantTeamMembership,
  resendTeamInvite,
  revokeAdminTeamMembership,
  revokeTeamInvite,
  revokeTeamMembership,
} from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useToast } from "@/components/ui/toast";
import { unscopedKey } from "@/lib/queryClient";
import { useMe } from "@/features/auth/useMe";
import { useScopeStore } from "@/stores/scope";
import { useRoster } from "@/features/coach/hooks/useRoster";
import type {
  AdminTeamMembership,
  Player,
  TeamInviteRow,
} from "@/lib/types";

/**
 * M8b E1–E8 — `/admin/people` (membership-scoped people console).
 *
 * Capability gate: page hidden unless the active scope has the
 * `membership:manage` capability OR the user is a global admin
 * (the M8a foundation per-section gate prevents direct nav too).
 *
 * Layout: two tabs (Members + Invites). Members lists every
 * membership on the active team with role-change + remove
 * controls; Invites lists every pending/historical invite with
 * resend + revoke + dev-token copy-link, plus the composer.
 *
 * Both lists key off the active team from the scope store —
 * NOT scope-injected because the backend takes `team_id` as
 * an explicit query param (the same user can be a team admin
 * on multiple teams; the page renders the active one).
 */

const ROLE_ORDER: Record<string, number> = {
  team_admin: 0,
  coach: 1,
  assistant_coach: 2,
  player: 3,
  guardian: 4,
};

const INVITE_ROLES = [
  { value: "team_admin", label: "Team admin" },
  { value: "coach", label: "Coach" },
  { value: "assistant_coach", label: "Assistant coach" },
  { value: "player", label: "Player" },
  { value: "guardian", label: "Guardian" },
];

const MEMBER_ROLES = INVITE_ROLES; // Same set; the modal reuses.

export function AdminPeoplePage() {
  const me = useMe().data;
  const scopeTeamId = useScopeStore((s) => s.teamId);
  const isGlobalAdmin = !!me?.user?.is_global_admin;
  const hasManageCapability = me?.active_scope?.capabilities?.includes(
    "membership:manage",
  );
  const canManage = !!(isGlobalAdmin || hasManageCapability);

  // Global admins typically have no team membership of their own (the
  // `admin-global` seed user is the canonical example). Let them pick
  // a team locally without touching the active scope — the backend
  // rejects scope-PUT for non-members, and `/api/admin/teams/{id}/*`
  // already exposes the cross-tenant CRUD a global admin needs. For
  // team admins, the active scope's team is the only one they can
  // manage, so we always use it.
  const [pickedTeamId, setPickedTeamId] = useState<string | null>(null);
  const teamId = isGlobalAdmin ? pickedTeamId : scopeTeamId;

  const [tab, setTab] = useState<"members" | "invites">("members");

  if (!teamId) {
    if (isGlobalAdmin) {
      return <TeamPicker onPick={setPickedTeamId} />;
    }
    return (
      <div data-testid="admin-people" className="p-4">
        <p className="text-ink-muted text-sm">
          No active team selected. Pick a team from the scope switcher to
          manage people.
        </p>
      </div>
    );
  }

  if (!canManage) {
    return (
      <div data-testid="admin-people" className="p-4">
        <p data-testid="admin-people-denied" className="text-destructive text-sm">
          You don't have permission to manage people on this team.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="admin-people" className="flex flex-col gap-3">
      <header className="flex items-baseline justify-between">
        <h2 className="text-base font-display uppercase tracking-wider text-ink">
          People
        </h2>
        {isGlobalAdmin ? (
          <button
            type="button"
            onClick={() => setPickedTeamId(null)}
            data-testid="admin-people-switch-team"
            className="text-[0.65rem] font-display uppercase tracking-wider text-ink-muted hover:text-accent"
          >
            ← Switch team
          </button>
        ) : null}
      </header>

      <nav
        data-testid="admin-people-tabs"
        role="tablist"
        className="inline-flex gap-1 border-b border-rule"
      >
        <TabButton
          active={tab === "members"}
          onClick={() => setTab("members")}
          testid="admin-people-tab-members"
        >
          Members
        </TabButton>
        <TabButton
          active={tab === "invites"}
          onClick={() => setTab("invites")}
          testid="admin-people-tab-invites"
        >
          Invites
        </TabButton>
      </nav>

      {/* Re-review P1: `key={teamId}` re-mounts the panel when the
       * active team changes so any dev-token / optimistic-row
       * state from the previous team is reset. Belt + suspenders
       * with the explicit per-team filter inside InvitesPanel. */}
      {tab === "members" ? (
        <MembersPanel
          key={teamId}
          teamId={teamId}
          isGlobalAdmin={isGlobalAdmin}
        />
      ) : (
        <InvitesPanel key={teamId} teamId={teamId} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Team picker — global-admin fallback when no team is active
// ---------------------------------------------------------------------------

/** In-page team picker for global admins with no active scope.
 *
 * Lists every team via `GET /api/admin/teams`. Picking one just sets
 * local component state — does NOT call `PUT /api/me/scope` because the
 * backend rejects scope-PUT for non-members (tenancy.save_active_scope
 * has no global-admin override). The page renders the per-team panels
 * against the locally-picked id, and the membership data endpoints
 * branch to their `/api/admin/teams/{id}/*` parallels for global admins.
 */
function TeamPicker({ onPick }: { onPick: (teamId: string) => void }) {
  const teamsQuery = useQuery({
    queryKey: unscopedKey("admin-teams"),
    queryFn: getAdminTeams,
    staleTime: 60_000,
  });

  return (
    <div
      data-testid="admin-people"
      className="flex flex-col gap-3"
    >
      <header className="flex items-baseline justify-between">
        <h2 className="text-base font-display uppercase tracking-wider text-ink">
          People
        </h2>
      </header>
      <section
        data-testid="admin-people-team-picker"
        className="flex flex-col gap-3 rounded border border-rule bg-surface shadow-sm p-4"
      >
        <div className="flex flex-col gap-1">
          <h3 className="text-sm font-display uppercase tracking-wider text-ink">
            Pick a team to manage
          </h3>
          <p className="text-xs text-ink-muted">
            Members and invites are scoped per team. Pick one to load its
            people console.
          </p>
        </div>

        {teamsQuery.isLoading ? (
          <p className="text-xs text-ink-muted">Loading teams…</p>
        ) : teamsQuery.isError ? (
          <p className="text-xs text-destructive">Failed to load teams.</p>
        ) : (teamsQuery.data ?? []).length === 0 ? (
          <p className="text-xs text-ink-muted">
            No teams yet. Create one from /admin/teams.
          </p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {[...(teamsQuery.data ?? [])]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((team) => (
                <li key={team.id}>
                  <button
                    type="button"
                    onClick={() => onPick(team.id)}
                    data-testid="admin-people-team-pick"
                    data-team-id={team.id}
                    className="flex w-full items-center justify-between rounded border border-rule bg-surface px-3 py-2 text-left hover:border-accent transition-colors"
                  >
                    <span className="flex flex-col">
                      <span className="text-sm text-ink">{team.name}</span>
                      <span className="text-[0.65rem] font-mono uppercase tracking-wider text-ink-muted">
                        {team.slug} · {team.game_format}
                      </span>
                    </span>
                    <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                      Manage →
                    </span>
                  </button>
                </li>
              ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  testid,
  children,
}: {
  active: boolean;
  onClick: () => void;
  testid: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testid}
      data-active={active ? "true" : undefined}
      className="px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink-muted hover:text-ink data-[active=true]:text-ink data-[active=true]:border-b-2 data-[active=true]:border-accent transition-colors"
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// E2 / E3 / E4 — Members panel
// ---------------------------------------------------------------------------

function MembersPanel({
  teamId,
  isGlobalAdmin,
}: {
  teamId: string;
  isGlobalAdmin: boolean;
}) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: unscopedKey("admin-people-memberships", { teamId }),
    // Global admins hit the `/api/admin/teams/{id}/memberships` parallel
    // because the team-scoped `/api/team/memberships` requires team_admin
    // membership and a global admin typically has none.
    queryFn: () =>
      isGlobalAdmin
        ? getAdminTeamMemberships(teamId)
        : getTeamMemberships(teamId),
    staleTime: 60_000,
  });

  const [removeTarget, setRemoveTarget] = useState<AdminTeamMembership | null>(
    null,
  );
  const [changeRoleTarget, setChangeRoleTarget] =
    useState<AdminTeamMembership | null>(null);

  const sorted = useMemo(() => {
    const rows = query.data ?? [];
    return [...rows].sort((a, b) => {
      const ar = ROLE_ORDER[a.role] ?? 99;
      const br = ROLE_ORDER[b.role] ?? 99;
      if (ar !== br) return ar - br;
      return (a.username ?? "").localeCompare(b.username ?? "");
    });
  }, [query.data]);

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: unscopedKey("admin-people-memberships", { teamId }),
    });

  return (
    <section
      data-testid="admin-people-members"
      className="flex flex-col gap-2 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <header className="flex items-baseline justify-between">
        <h3 className="text-sm font-display uppercase tracking-wider text-ink">
          Team members
        </h3>
        <span className="text-[0.6rem] font-mono text-ink-muted">
          {query.isFetching ? "Refreshing…" : `${sorted.length} member${sorted.length === 1 ? "" : "s"}`}
        </span>
      </header>

      {query.error ? (
        <p
          data-testid="admin-people-members-error"
          className="text-destructive font-mono text-xs"
        >
          Failed to load memberships.
        </p>
      ) : null}

      {sorted.length === 0 && !query.isLoading ? (
        <p
          data-testid="admin-people-members-empty"
          className="text-xs font-mono text-ink-muted"
        >
          No members yet.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {sorted.map((m) => (
            <li
              key={m.id}
              data-testid="admin-people-member-row"
              data-membership-id={m.id}
              data-role={m.role}
              className="grid grid-cols-[1fr_auto_auto] gap-2 items-center rounded border border-rule/60 bg-elevated px-2 py-1.5 text-xs"
            >
              <div className="min-w-0">
                <div className="text-ink truncate">
                  {m.display_name || m.username}{" "}
                  <span className="text-ink-muted">@{m.username}</span>
                </div>
                <div className="text-[0.6rem] font-mono text-ink-muted">
                  member since {m.created_at}
                </div>
              </div>
              <RolePill role={m.role} />
              <div className="inline-flex gap-1">
                <button
                  type="button"
                  onClick={() => setChangeRoleTarget(m)}
                  data-testid="admin-people-change-role"
                  className="rounded border border-rule text-ink-muted hover:text-ink hover:bg-field px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider"
                >
                  Change role
                </button>
                <button
                  type="button"
                  onClick={() => setRemoveTarget(m)}
                  data-testid="admin-people-remove"
                  className="rounded border border-destructive/40 text-destructive hover:bg-destructive/10 px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider"
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {removeTarget ? (
        <RemoveMemberConfirm
          teamId={teamId}
          isGlobalAdmin={isGlobalAdmin}
          membership={removeTarget}
          onClose={() => setRemoveTarget(null)}
          onSuccess={() => {
            setRemoveTarget(null);
            void invalidate();
          }}
        />
      ) : null}

      {changeRoleTarget ? (
        <ChangeRoleModal
          teamId={teamId}
          isGlobalAdmin={isGlobalAdmin}
          membership={changeRoleTarget}
          onClose={() => setChangeRoleTarget(null)}
          onSuccess={() => {
            setChangeRoleTarget(null);
            void invalidate();
          }}
        />
      ) : null}
    </section>
  );
}

function RolePill({ role }: { role: string }) {
  const colors: Record<string, string> = {
    team_admin: "border-accent text-accent",
    coach: "border-good/40 text-good",
    assistant_coach: "border-good/40 text-good",
    player: "border-rule text-ink",
    guardian: "border-rule text-ink-muted",
  };
  const tone = colors[role] ?? "border-rule text-ink-muted";
  const label =
    INVITE_ROLES.find((r) => r.value === role)?.label ?? role.replace("_", " ");
  return (
    <span
      data-testid="admin-people-role-pill"
      data-role={role}
      className={`rounded border bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider ${tone}`}
    >
      {label}
    </span>
  );
}

function RemoveMemberConfirm({
  teamId,
  isGlobalAdmin,
  membership,
  onClose,
  onSuccess,
}: {
  teamId: string;
  isGlobalAdmin: boolean;
  membership: AdminTeamMembership;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () =>
      isGlobalAdmin
        ? revokeAdminTeamMembership(teamId, membership.id)
        : revokeTeamMembership(membership.id, teamId),
    onSuccess: () => {
      toast.success("Member removed");
      onSuccess();
    },
    onError: (err) => {
      setError(formatLastAdminErr(err));
      toast.error("Couldn't remove member", {
        description: formatLastAdminErr(err),
      });
    },
  });
  return (
    <ConfirmDialog
      title="Remove team member?"
      message={
        <>
          <p>
            <strong>{membership.display_name || membership.username}</strong>{" "}
            (@{membership.username}) loses their{" "}
            <strong>{membership.role.replace("_", " ")}</strong> role on this
            team. Their account and coaching data are not deleted.
          </p>
          {error ? (
            <p
              data-testid="admin-people-remove-error"
              className="mt-2 text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </>
      }
      confirmLabel={mut.isPending ? "Removing…" : "Remove member"}
      danger
      onConfirm={() => {
        setError(null);
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-people-remove-confirm"
    />
  );
}

// E3 — Change role: revoke + re-grant. UX uplift over legacy
// `promptChoice` — styled chooser inside a CoachModal-style shell.
function ChangeRoleModal({
  teamId,
  isGlobalAdmin,
  membership,
  onClose,
  onSuccess,
}: {
  teamId: string;
  isGlobalAdmin: boolean;
  membership: AdminTeamMembership;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const toast = useToast();
  const [selectedRole, setSelectedRole] = useState(membership.role);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      // Revoke + re-grant — backend has no PATCH for role change.
      // Global admins use the admin-tenants endpoints; team admins use
      // the membership-scoped ones.
      //
      // Two-step is non-atomic: if revoke succeeds but grant fails (a
      // 409 last-admin on the regrant, a transient backend error, a
      // dropped connection), the user is left with no membership.
      // We track which step failed so the error copy can tell the
      // operator the membership was removed and prompt a retry.
      let revoked = false;
      try {
        if (isGlobalAdmin) {
          await revokeAdminTeamMembership(teamId, membership.id);
          revoked = true;
          return await grantAdminTeamMembership(teamId, {
            user_id: membership.user_id,
            role: selectedRole,
          });
        }
        await revokeTeamMembership(membership.id, teamId);
        revoked = true;
        return await grantTeamMembership({
          team_id: teamId,
          user_id: membership.user_id,
          role: selectedRole,
        });
      } catch (err) {
        if (revoked) {
          // Annotate so onError can surface partial-failure copy.
          (err as { __partialRevoke?: boolean }).__partialRevoke = true;
        }
        throw err;
      }
    },
    onSuccess: () => {
      toast.success("Role updated");
      onSuccess();
    },
    onError: (err) => {
      const partial = (err as { __partialRevoke?: boolean }).__partialRevoke;
      const base = formatLastAdminErr(err);
      setError(
        partial
          ? `${base} The previous role was already removed — re-grant the role manually from this dialog.`
          : base,
      );
      toast.error("Couldn't update role", { description: base });
    },
  });

  return (
    <ConfirmDialog
      title="Change role"
      message={
        <div className="flex flex-col gap-2">
          <p className="text-ink">
            Update <strong>{membership.display_name || membership.username}</strong>
            's role on this team.
          </p>
          <div
            role="group"
            aria-label="Role"
            data-testid="admin-people-role-chooser"
            className="flex flex-wrap gap-1"
          >
            {MEMBER_ROLES.map((r) => (
              <button
                key={r.value}
                type="button"
                onClick={() => setSelectedRole(r.value)}
                data-testid={`admin-people-role-option-${r.value}`}
                data-active={selectedRole === r.value ? "true" : undefined}
                className="rounded border px-3 py-1 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink"
              >
                {r.label}
              </button>
            ))}
          </div>
          {error ? (
            <p
              data-testid="admin-people-change-role-error"
              className="text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
          <p className="text-[0.6rem] font-mono text-ink-muted">
            Implemented as revoke + re-grant on the backend. Last admin
            protection still applies.
          </p>
        </div>
      }
      confirmLabel={mut.isPending ? "Saving…" : "Update role"}
      onConfirm={() => {
        setError(null);
        if (selectedRole === membership.role) {
          onClose();
          return;
        }
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-people-change-role-confirm"
    />
  );
}

// ---------------------------------------------------------------------------
// E5 / E6 / E7 — Invites panel
// ---------------------------------------------------------------------------

function InvitesPanel({ teamId }: { teamId: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: unscopedKey("admin-people-invites", { teamId }),
    queryFn: () => getTeamInvites(teamId),
  });
  const [composerOpen, setComposerOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<TeamInviteRow | null>(null);
  // E7 — dev-token surface: stash rows that came back from a
  // create/resend with `invite_token` so the operator can copy them.
  // We hold the full row optimistically too — if a fresh create
  // hasn't been picked up by the list refetch yet (the row is
  // brand-new), the optimistic copy keeps the dev-link visible
  // until the GET catches up. Keyed by invite id so a refresh
  // overwrites the prior URL.
  const [devTokens, setDevTokens] = useState<Record<string, string>>({});
  const [optimisticRows, setOptimisticRows] = useState<TeamInviteRow[]>([]);

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: unscopedKey("admin-people-invites", { teamId }),
    });

  const resend = useMutation({
    mutationFn: (inviteId: string) => resendTeamInvite(inviteId, teamId),
    onSuccess: (row) => {
      if (row.invite_token) {
        setDevTokens((m) => ({ ...m, [row.id]: row.invite_token! }));
      }
      toast.success("Invite resent");
      void invalidate();
    },
    onError: (err) => {
      toast.error("Couldn't resend invite", { description: formatErr(err) });
    },
  });

  // Merge optimistic rows + server list, deduped by id (server
  // wins once the GET resolves). Re-review P1 defense-in-depth:
  // filter optimistic rows by `team_id` so a stale entry from a
  // different team can never leak into the rendered list, even if
  // the parent forgets to re-mount on teamId change.
  const visibleRows: TeamInviteRow[] = useMemo(() => {
    const fromServer = query.data ?? [];
    const knownIds = new Set(fromServer.map((r) => r.id));
    const stillPending = optimisticRows.filter(
      (r) => r.team_id === teamId && !knownIds.has(r.id),
    );
    return [...stillPending, ...fromServer];
  }, [query.data, optimisticRows, teamId]);

  return (
    <section
      data-testid="admin-people-invites"
      className="flex flex-col gap-2 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <header className="flex items-baseline justify-between">
        <h3 className="text-sm font-display uppercase tracking-wider text-ink">
          Invites
        </h3>
        <button
          type="button"
          onClick={() => setComposerOpen(true)}
          data-testid="admin-people-invite-new"
          className="rounded border border-accent bg-accent text-accent-ink hover:bg-accent-hover px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider"
        >
          Send invite
        </button>
      </header>

      {query.error ? (
        <p
          data-testid="admin-people-invites-error"
          className="text-destructive font-mono text-xs"
        >
          Failed to load invites.
        </p>
      ) : null}

      {visibleRows.length === 0 && !query.isLoading ? (
        <p
          data-testid="admin-people-invites-empty"
          className="text-xs font-mono text-ink-muted"
        >
          No invites sent yet.
        </p>
      ) : null}

      {visibleRows.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {visibleRows.map((invite) => (
            <InviteRow
              key={invite.id}
              invite={invite}
              devTokenUrl={devTokens[invite.id]}
              onResend={() => resend.mutate(invite.id)}
              onRevoke={() => setRevokeTarget(invite)}
              resendPending={resend.isPending && resend.variables === invite.id}
            />
          ))}
        </ul>
      ) : null}

      {composerOpen ? (
        <InviteComposerModal
          teamId={teamId}
          onClose={() => setComposerOpen(false)}
          onSuccess={(row) => {
            if (row.invite_token) {
              setDevTokens((m) => ({ ...m, [row.id]: row.invite_token! }));
            }
            // Optimistic insert — the dev-link surfaces immediately
            // even before the list refetch resolves. Once the GET
            // returns, the server row replaces this entry (matched
            // by id, deduped in `visibleRows`).
            setOptimisticRows((rows) => [row, ...rows.filter((r) => r.id !== row.id)]);
            setComposerOpen(false);
            void invalidate();
          }}
        />
      ) : null}

      {revokeTarget ? (
        <RevokeInviteConfirm
          teamId={teamId}
          invite={revokeTarget}
          onClose={() => setRevokeTarget(null)}
          onSuccess={() => {
            setRevokeTarget(null);
            void invalidate();
          }}
        />
      ) : null}
    </section>
  );
}

function InviteRow({
  invite,
  devTokenUrl,
  onResend,
  onRevoke,
  resendPending,
}: {
  invite: TeamInviteRow;
  devTokenUrl: string | undefined;
  onResend: () => void;
  onRevoke: () => void;
  resendPending: boolean;
}) {
  const expired =
    invite.expires_at != null && invite.expires_at * 1000 < Date.now();
  const status = invite.status || (expired ? "expired" : "pending");
  const isPending = status === "pending" && !expired;
  return (
    <li
      data-testid="admin-people-invite-row"
      data-invite-id={invite.id}
      data-invite-status={status}
      className="rounded border border-rule/60 bg-elevated px-2 py-1.5"
    >
      <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-center text-xs">
        <div className="min-w-0">
          <div className="text-ink truncate">{invite.email}</div>
          <div className="text-[0.6rem] font-mono text-ink-muted">
            {expiryLabel(invite.expires_at)}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <RolePill role={invite.role} />
          <InviteStatusPill status={status} />
          <DeliveryPill
            status={invite.last_delivery_status ?? null}
            isPending={isPending}
          />
        </div>
        <div className="inline-flex gap-1">
          {isPending ? (
            <button
              type="button"
              onClick={onResend}
              disabled={resendPending}
              data-testid="admin-people-invite-resend"
              className="rounded border border-rule text-ink-muted hover:text-ink hover:bg-field px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {resendPending ? "Resending…" : "Resend"}
            </button>
          ) : null}
          {isPending ? (
            <button
              type="button"
              onClick={onRevoke}
              data-testid="admin-people-invite-revoke"
              className="rounded border border-destructive/40 text-destructive hover:bg-destructive/10 px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider"
            >
              Revoke
            </button>
          ) : null}
        </div>
      </div>
      {/* E7 — dev-token copy-link, rendered ONCE per row on the row
       * that just got a fresh token (never logged, never in the
       * success banner — gated control only). */}
      {devTokenUrl ? (
        <div
          data-testid="admin-people-invite-dev-link"
          data-invite-id={invite.id}
          className="mt-1.5 flex items-center gap-1 text-[0.6rem] font-mono"
        >
          <span className="text-ink-muted">Dev invite URL:</span>
          <code className="text-ink break-all">{devTokenUrl}</code>
          <button
            type="button"
            onClick={() =>
              navigator.clipboard?.writeText(buildInviteUrl(devTokenUrl))
            }
            data-testid="admin-people-invite-dev-copy"
            className="rounded border border-rule text-ink-muted hover:text-ink hover:bg-field px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider"
          >
            Copy link
          </button>
        </div>
      ) : null}
    </li>
  );
}

function InviteStatusPill({ status }: { status: string }) {
  const tone =
    status === "pending"
      ? "border-accent text-accent"
      : status === "accepted"
        ? "border-good/40 text-good"
        : status === "revoked"
          ? "border-destructive/40 text-destructive"
          : "border-rule text-ink-muted";
  return (
    <span
      data-testid="admin-people-invite-status-pill"
      data-status={status}
      className={`rounded border bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider ${tone}`}
    >
      {status}
    </span>
  );
}

// E5 — Delivery pill enumerates the four legacy `last_delivery_status`
// values: sent / disabled / not_configured / failed.
function DeliveryPill({
  status,
  isPending,
}: {
  status: string | null;
  isPending: boolean;
}) {
  if (!isPending) return null;
  const map: Record<string, { label: string; tone: string }> = {
    sent: { label: "Email sent", tone: "border-good/40 text-good" },
    disabled: { label: "Email off", tone: "border-rule text-ink-muted" },
    not_configured: {
      label: "Email not configured",
      tone: "border-rule text-ink-muted",
    },
    failed: { label: "Send failed", tone: "border-destructive/40 text-destructive" },
  };
  const entry = status ? map[status] : null;
  if (!entry) {
    return (
      <span
        data-testid="admin-people-invite-delivery-pill"
        data-delivery="awaiting"
        className="rounded border border-rule bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider text-ink-muted"
      >
        awaiting
      </span>
    );
  }
  return (
    <span
      data-testid="admin-people-invite-delivery-pill"
      data-delivery={status}
      className={`rounded border bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider ${entry.tone}`}
    >
      {entry.label}
    </span>
  );
}

function expiryLabel(expiresAt: number | null | undefined): string {
  if (!expiresAt) return "no expiry on file";
  const ms = expiresAt * 1000;
  const diff = ms - Date.now();
  if (diff <= 0) return "expired";
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days >= 1) return `expires in ${days}d`;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours >= 1) return `expires in ${hours}h`;
  return "expires in <1h";
}

function buildInviteUrl(token: string): string {
  // The Tktic SPA's invite-accept landing is `/invite/{token}` (a
  // route registered in router.tsx in M9). For dev-token copies
  // we surface the same path — the M9 landing handles both the
  // signed-in accept and the create-account branches.
  if (typeof window === "undefined") return `/invite/${token}`;
  return `${window.location.origin}/invite/${token}`;
}

function InviteComposerModal({
  teamId,
  onClose,
  onSuccess,
}: {
  teamId: string;
  onClose: () => void;
  onSuccess: (row: TeamInviteRow) => void;
}) {
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("coach");
  const [playerIds, setPlayerIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Roster is fetched only when role is player/guardian (the only
  // roles that surface the multi-select). Avoids the empty-roster
  // 200 round-trip on every composer open.
  const rosterEnabled = role === "player" || role === "guardian";
  const rosterQuery = useRoster();
  const players: Player[] = rosterEnabled
    ? rosterQuery.data?.players ?? []
    : [];

  const mut = useMutation({
    mutationFn: () =>
      createTeamInvite({
        team_id: teamId,
        email,
        role,
        player_ids:
          role === "player" || role === "guardian" ? playerIds : undefined,
      }),
    onSuccess: (row) => {
      toast.success("Invite sent");
      onSuccess(row);
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't send invite", { description: formatErr(err) });
    },
  });

  return (
    <ConfirmDialog
      title="Send invite"
      message={
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Email
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-people-invite-email"
              required
              className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </label>

          <div
            role="group"
            aria-label="Role"
            data-testid="admin-people-invite-role"
            className="flex flex-col gap-1"
          >
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Role
            </span>
            <div className="flex flex-wrap gap-1">
              {INVITE_ROLES.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => {
                    setRole(r.value);
                    if (r.value !== "player" && r.value !== "guardian") {
                      setPlayerIds([]);
                    }
                  }}
                  data-testid={`admin-people-invite-role-${r.value}`}
                  data-active={role === r.value ? "true" : undefined}
                  className="rounded border px-3 py-1 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink"
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {rosterEnabled ? (
            <div className="flex flex-col gap-1">
              <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                Link to players
              </span>
              {players.length === 0 ? (
                <p className="text-[0.6rem] font-mono text-ink-muted">
                  {rosterQuery.isLoading
                    ? "Loading roster…"
                    : "No players on roster yet."}
                </p>
              ) : (
                <div
                  data-testid="admin-people-invite-player-list"
                  className="flex flex-col gap-0.5 max-h-40 overflow-auto rounded border border-rule bg-elevated p-1"
                >
                  {players.map((p) => {
                    const selected = playerIds.includes(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          setPlayerIds((ids) =>
                            selected
                              ? ids.filter((x) => x !== p.id)
                              : [...ids, p.id],
                          );
                        }}
                        data-testid={`admin-people-invite-player-${p.id}`}
                        data-active={selected ? "true" : undefined}
                        className="flex items-center gap-1 w-full rounded px-1 py-0.5 text-xs text-left transition-colors hover:bg-field data-[active=true]:text-accent"
                      >
                        <span className="text-ink">
                          {p.display_name || p.id}
                          {p.jersey_number ? (
                            <span className="text-ink-muted">
                              {" "}
                              #{p.jersey_number}
                            </span>
                          ) : null}
                        </span>
                        {selected ? <span className="ml-auto text-accent">✓</span> : null}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : null}

          {error ? (
            <p
              data-testid="admin-people-invite-error"
              className="text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={mut.isPending ? "Sending…" : "Send invite"}
      onConfirm={() => {
        setError(null);
        if (!email.trim() || email.length < 3) {
          setError("Email is required.");
          return;
        }
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-people-invite-composer"
    />
  );
}

function RevokeInviteConfirm({
  teamId,
  invite,
  onClose,
  onSuccess,
}: {
  teamId: string;
  invite: TeamInviteRow;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => revokeTeamInvite(invite.id, teamId),
    onSuccess: () => {
      toast.success("Invite revoked");
      onSuccess();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't revoke invite", { description: formatErr(err) });
    },
  });
  return (
    <ConfirmDialog
      title="Revoke invite?"
      message={
        <>
          <p>
            The invite to <strong>{invite.email}</strong> will be cancelled.
            Any pending link becomes invalid.
          </p>
          {error ? (
            <p
              data-testid="admin-people-invite-revoke-error"
              className="mt-2 text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </>
      }
      confirmLabel={mut.isPending ? "Revoking…" : "Revoke invite"}
      danger
      onConfirm={() => {
        setError(null);
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-people-invite-revoke-confirm"
    />
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string" ? err.detail : `HTTP ${err.status}`;
  }
  return "Request failed";
}

/** 409 last-admin variant — friendly inline copy. The backend's
 * detail string varies ("A team must keep at least one team
 * admin" / "Cannot remove the last team admin"); the helper
 * preserves the server-side detail string when it's a string. */
function formatLastAdminErr(err: unknown): string {
  if (err instanceof ApiError && err.status === 409) {
    return typeof err.detail === "string"
      ? err.detail
      : "A team must keep at least one team admin.";
  }
  return formatErr(err);
}
